import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, X, ArrowRight, RotateCcw, Check } from 'lucide-react';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Utility helpers for YYYY-MM-DD
const formatDateStr = (d) => {
    if (!d) return '';
    const dateObj = typeof d === 'string' ? parseDateStr(d) : d;
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const parseDateStr = (str) => {
    if (!str) return null;
    const cleanStr = String(str).split('T')[0];
    const parts = cleanStr.split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    return new Date(y, m, d);
};

const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
};

const formatDisplayDate = (str) => {
    const d = parseDateStr(str);
    if (!d) return 'Select date';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const CustomDatePicker = ({
    mode = 'range', // 'range' | 'single'
    startDate = '',
    endDate = '',
    value = '',
    onStartChange,
    onEndChange,
    onChange,
    disabled = false,
    className = '',
    quickFilters = true,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);
    const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

    // Active state
    const currentStart = mode === 'range' ? (startDate || value) : value;
    const currentEnd = mode === 'range' ? (endDate || startDate || value) : value;

    const parsedStart = parseDateStr(currentStart);
    const parsedEnd = parseDateStr(currentEnd);

    // Viewport month & year state
    const today = new Date();
    const [viewYear, setViewYear] = useState((parsedStart || today).getFullYear());
    const [viewMonth, setViewMonth] = useState((parsedStart || today).getMonth());

    // Selecting state for range selection
    const [tempStart, setTempStart] = useState(parsedStart);
    const [tempEnd, setTempEnd] = useState(parsedEnd);
    const [selectingStep, setSelectingStep] = useState('start'); // 'start' | 'end'

    // Calculate fixed position on screen via Portal
    const updatePosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const popoverWidth = 320;
            const popoverHeight = 360;

            let top = rect.bottom + 6;
            let left = rect.left;

            // Flip above if close to bottom of screen
            if (rect.bottom + popoverHeight > window.innerHeight && rect.top > popoverHeight) {
                top = rect.top - popoverHeight - 6;
            }

            // Adjust left if overflowing screen width
            if (left + popoverWidth > window.innerWidth - 12) {
                left = Math.max(12, window.innerWidth - popoverWidth - 12);
            }

            setPopoverPos({ top, left });
        }
    };

    // Sync state when props change or popover opens
    useEffect(() => {
        if (isOpen) {
            updatePosition();
            const startD = parseDateStr(mode === 'range' ? startDate : value);
            const endD = parseDateStr(mode === 'range' ? endDate : value);
            setTempStart(startD);
            setTempEnd(endD);
            if (startD) {
                setViewYear(startD.getFullYear());
                setViewMonth(startD.getMonth());
            }
            setSelectingStep('start');
        }
    }, [isOpen, startDate, endDate, value, mode]);

    // Update position on scroll or window resize
    useEffect(() => {
        if (isOpen) {
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen]);

    // Close on click outside or Escape
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Navigation
    const handlePrevMonth = () => {
        if (viewMonth === 0) {
            setViewMonth(11);
            setViewYear(v => v - 1);
        } else {
            setViewMonth(v => v - 1);
        }
    };

    const handleNextMonth = () => {
        if (viewMonth === 11) {
            setViewMonth(0);
            setViewYear(v => v + 1);
        } else {
            setViewMonth(v => v + 1);
        }
    };

    const handleJumpToToday = () => {
        const now = new Date();
        setViewYear(now.getFullYear());
        setViewMonth(now.getMonth());
        if (mode === 'single') {
            const str = formatDateStr(now);
            if (onChange) onChange(str);
            if (onStartChange) onStartChange(str);
            setIsOpen(false);
        }
    };

    // Date Click Handler
    const handleDateClick = (date) => {
        if (mode === 'single') {
            const str = formatDateStr(date);
            setTempStart(date);
            setTempEnd(date);
            if (onChange) onChange(str);
            if (onStartChange) onStartChange(str);
            setIsOpen(false);
            return;
        }

        // Range Mode logic
        if (selectingStep === 'start' || !tempStart || (tempStart && tempEnd)) {
            setTempStart(date);
            setTempEnd(null);
            setSelectingStep('end');
        } else {
            // Selecting end date
            if (date < tempStart) {
                // If clicked date is before start date, set it as new start
                setTempStart(date);
                setTempEnd(null);
                setSelectingStep('end');
            } else {
                setTempEnd(date);
                const sStr = formatDateStr(tempStart);
                const eStr = formatDateStr(date);
                if (onStartChange) onStartChange(sStr);
                if (onEndChange) onEndChange(eStr);
                if (onChange) onChange({ startDate: sStr, endDate: eStr });
                setIsOpen(false);
                setSelectingStep('start');
            }
        }
    };

    // Quick Filters
    const applyQuickPreset = (preset) => {
        const now = new Date();
        let s = new Date();
        let e = new Date();

        if (preset === 'today') {
            s = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            e = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (preset === 'yesterday') {
            s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
            e = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        } else if (preset === 'last7') {
            s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
            e = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (preset === 'thisMonth') {
            s = new Date(now.getFullYear(), now.getMonth(), 1);
            e = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (preset === 'last30') {
            s = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
            e = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        }

        const sStr = formatDateStr(s);
        const eStr = formatDateStr(e);

        setTempStart(s);
        setTempEnd(e);
        setViewYear(s.getFullYear());
        setViewMonth(s.getMonth());

        if (mode === 'single') {
            if (onChange) onChange(sStr);
            if (onStartChange) onStartChange(sStr);
        } else {
            if (onStartChange) onStartChange(sStr);
            if (onEndChange) onEndChange(eStr);
            if (onChange) onChange({ startDate: sStr, endDate: eStr });
        }
        setIsOpen(false);
    };

    // Calculate calendar grid days
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const calendarCells = [];

    // Prev month padding days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        const pDay = daysInPrevMonth - i;
        calendarCells.push({
            date: new Date(viewYear, viewMonth - 1, pDay),
            isCurrentMonth: false,
        });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
        calendarCells.push({
            date: new Date(viewYear, viewMonth, day),
            isCurrentMonth: true,
        });
    }

    // Next month padding days to complete 6 rows (42 days)
    const remainingCells = 42 - calendarCells.length;
    for (let day = 1; day <= remainingCells; day++) {
        calendarCells.push({
            date: new Date(viewYear, viewMonth + 1, day),
            isCurrentMonth: false,
        });
    }

    return (
        <div className={`inline-block ${className}`}>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isOpen
                        ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/50 dark:bg-blue-950/40 text-blue-900 dark:text-white'
                        : 'border-[var(--color-border)] bg-[var(--color-bg-card)] hover:bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <Calendar size={15} className="text-blue-600 dark:text-blue-400 shrink-0" />
                
                {mode === 'range' ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                        <span>{formatDisplayDate(currentStart)}</span>
                        <ArrowRight size={12} className="text-slate-400 shrink-0" />
                        <span>{formatDisplayDate(currentEnd)}</span>
                    </div>
                ) : (
                    <span>{formatDisplayDate(currentStart)}</span>
                )}
            </button>

            {/* Calendar Popover via Portal */}
            {isOpen && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                    className="fixed z-[99999] w-72 sm:w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-top-2 duration-150"
                >
                    {/* Header: Month & Navigation */}
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-1">
                            <span className="font-extrabold text-sm text-slate-900 dark:text-white">
                                {MONTH_NAMES[viewMonth]} {viewYear}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={handleJumpToToday}
                                className="px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-wider hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors mr-1"
                                title="Go to today"
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={handlePrevMonth}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={handleNextMonth}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Quick Range Presets (for range mode) */}
                    {mode === 'range' && quickFilters && (
                        <div className="flex items-center gap-1 overflow-x-auto pb-2.5 mb-2 custom-scrollbar border-b border-slate-100 dark:border-slate-800">
                            {[
                                { id: 'today', label: 'Today' },
                                { id: 'yesterday', label: 'Yesterday' },
                                { id: 'last7', label: '7 Days' },
                                { id: 'thisMonth', label: 'This Month' },
                                { id: 'last30', label: '30 Days' },
                            ].map(preset => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => applyQuickPreset(preset.id)}
                                    className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:text-blue-600 dark:hover:text-blue-400 text-slate-700 dark:text-slate-300 text-[10px] font-bold whitespace-nowrap transition-all"
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Weekday Labels */}
                    <div className="grid grid-cols-7 gap-1 text-center mb-1">
                        {DAYS_OF_WEEK.map(d => (
                            <span key={d} className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase">
                                {d}
                            </span>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 text-center">
                        {calendarCells.map((cell, idx) => {
                            const { date, isCurrentMonth } = cell;
                            const isTodayDate = isSameDay(date, today);
                            const isStart = tempStart && isSameDay(date, tempStart);
                            const isEnd = tempEnd && isSameDay(date, tempEnd);
                            const isInRange = mode === 'range' && tempStart && tempEnd && date >= tempStart && date <= tempEnd;

                            let buttonStyle = 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800';

                            if (!isCurrentMonth) {
                                buttonStyle = 'text-slate-300 dark:text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30';
                            } else if (isStart || isEnd) {
                                buttonStyle = 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black shadow-md scale-105 rounded-xl z-10';
                            } else if (isInRange) {
                                buttonStyle = 'bg-blue-50 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 font-bold rounded-lg';
                            } else if (isTodayDate) {
                                buttonStyle = 'border-2 border-blue-500 font-extrabold text-blue-600 dark:text-blue-400 rounded-xl';
                            }

                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleDateClick(date)}
                                    className={`h-8 w-8 mx-auto flex items-center justify-center text-xs font-semibold transition-all cursor-pointer ${buttonStyle}`}
                                >
                                    {date.getDate()}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer Status & Clear */}
                    <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                            {mode === 'range'
                                ? (selectingStep === 'end' && tempStart ? 'Select end date' : 'Select range')
                                : 'Select date'}
                        </span>

                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="px-3 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs shadow-xs transition-colors"
                        >
                            Done
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CustomDatePicker;
